<template>
    <form @submit.prevent="loginAttempt">
        <div
                class="form-group"
                :class="{error: $v.loginForm.email.$error}"
        >
            <input
                    type="email"
                    :placeholder="$t('placeholder.email')"
                    class="form-control"
                    :disabled="loading"
                    v-model="loginForm.email"
                    @input="$v.loginForm.email.$touch"
            >
            <template v-if="$v.loginForm.email.$error">
                <p
                        class="error-message"
                        v-if="!$v.loginForm.email.email"
                >
                    {{$t('validations.email', {field: $t('fields.email')})}}
                </p>
                <p
                        class="error-message"
                        v-if="!$v.loginForm.email.required"
                >
                    {{$t('validations.required', {field: $t('fields.email')})}}
                </p>
            </template>
        </div>
        <template
                v-if="!forgotPassword"
        >
            <div
                    class="form-group"
                    :class="{error: $v.loginForm.password.$error}"
            >
                <input
                        class="form-control"
                        :disabled="loading"
                        type="password"
                        v-model="loginForm.password"
                        :placeholder="$t('placeholder.password')"
                        @input="$v.loginForm.password.$touch"
                >
                <template v-if="$v.loginForm.password.$error">
                    <p
                            class="error-message"
                            v-if="!$v.loginForm.password.required"
                    >
                        {{$t('validations.required', {field: $t('fields.password')})}}
                    </p>
                </template>
            </div>
        </template>

        <div class="captcha-container" v-if="recaptchaPublicKey && !forgotPassword">
            <vue-recaptcha
                    ref="recaptcha"
                    :sitekey="recaptchaPublicKey"
                    :loadRecaptchaScript="true"
                    :theme="'dark'"
                    @verify="loginForm.recaptcha = $event"
                    @expire="loginForm.recaptcha = null"
            />
            <template v-if="$v.loginForm.recaptcha.$error">
                <p
                        class="error-message"
                        v-if="!$v.loginForm.recaptcha.required"
                >
                    {{$t('validations.captcha')}}
                </p>
            </template>
        </div>

        <div class="messages">
            <div
                    v-for="(errors, errorKey) in loginErrors"
                    :key="errorKey"
            >
                <p
                        v-for="error in errors"
                        :key="error"
                >
                    {{error}}
                </p>
            </div>
        </div>

        <div
                class="buttons"
                v-if="!loading"
        >
            <button
                    v-if="!forgotPassword"
                    type="submit"
                    class="btn-login"
            >
                {{$t('login.buttons.login')}}
            </button>
            <button
                    v-else
                    type="button"
                    class="btn-login"
                    @click.prevent="recoverAttempt"
            >
                {{$t('login.buttons.recover')}}
            </button>
        </div>
        <Spinner v-else/>

        <div class="password-options">
            <template v-if="!forgotPassword">
                <a href="#!" @click.prevent="forgotPassword = true">
                    {{$t('login.buttons.forgot_password')}}
                </a>
            </template>
            <template v-else>
                <a href="#!" @click.prevent="forgotPassword = false">
                    {{$t('login.buttons.remember_password')}}
                </a>
            </template>
        </div>
    </form>
</template>

<script>
  import Spinner from "./Spinner";
  import { required, minLength, email, requiredIf } from 'vuelidate/lib/validators';
  import EventBus from '../eventbus';
  import languages from './../lang/languages';
  import VueRecaptcha from 'vue-recaptcha';

  export default {
      components: {
          Spinner,
          VueRecaptcha,
      },
      data(){
          return {
              loginForm: {
                  email: '',
                  password: '',
                  recaptcha: null,
              },
              loginErrors: {},
              loading: false,
              forgotPassword: false,
          }
      },
      methods: {
          loginAttempt(){
              if(this.$v.loginForm.$invalid){
                  return this.$v.loginForm.$touch();
              }

              this.loading = true;
              this.loginErrors = [];

              this.$auth.login({
                  body: this.loginForm,
                  rememberMe: true,
                  success: function (resp) {
                      this.loading = false;

                      let lastSelectedLang = languages.find(lang => resp.data.last_lang_code === lang.code);

                      if(lastSelectedLang){
                          EventBus.changeLanguage(lastSelectedLang);
                      }

                      this.$emit('successfullLogin', resp);
                  },
                  error: function (resp) {
                      this.loading = false;

                      if(this.recaptchaPublicKey){
                          this.$refs.recaptcha.reset();
                          this.loginForm.recaptcha = null;
                          //TODO: Unset dirty flag
                      }

                      if(resp.status === 423){ //Si no confirmo el email
                          EventBus.requireEmailVerification(() => {
                              this.$emit('successfullLogin');
                          });

                      }else if(resp.status === 403){
                          EventBus.userBanned();
                      }else if(resp.status === 401){
                          this.loginErrors = {email: [this.$t('login.invalid_credentials')]};
                          this.$emit('failedLogin', resp);
                      }else{
                          this.loginErrors = resp.data.errors || {general: [this.$t('error')]};
                          this.$emit('failedLogin', resp);
                      }
                  },
                  redirect: null,
              });
          },
          recoverAttempt(){
              if(this.$v.loginForm.email.$invalid){
                  return this.$v.loginForm.email.$touch();
              }

              this.loading = true;
              this.loginErrors = {};

              this.$http.post('auth/send_recover_email', {email: this.loginForm.email}).then(() => {
                  this.loading = false;
                  this.loginErrors = {recover: [this.$t('recover.sent')]};
                  this.forgotPassword = false;
              }).catch(() => {
                  this.loading = false;
                  this.loginErrors = {general: [this.$t('error')]};
              });
          }
      },
      mounted() {
          if(this.$route.query.recover){
              this.forgotPassword = true;
          }

          if(this.$route.query.email){
              this.loginForm.email = this.$route.query.email;
          }
      },
      computed: {
          recaptchaPublicKey(){
              return process.env.VUE_APP_RECAPTCHA_PUBLIC_KEY;
          }
      },
      validations() {
          return {
              loginForm: {
                  email: {
                      required,
                      email,
                      minLength: minLength(4)
                  },
                  password: {
                      required
                  },
                  recaptcha: {
                      required: requiredIf(() => this.recaptchaPublicKey)
                  }
              },
          }
      },
      watch: {
          loading(){
              this.$emit('loadingChange', this.loading);
          },
      },
  }
</script>

<style scoped lang="scss">
    @import '../styles/variables';
    @import '../styles/classes';

    .buttons{
        margin-bottom: 30px;
    }

    .btn-login, .btn-recover{
        @extend %fresh-btn;
        display: block;
        width: 100%;
        padding: 1em;
    }

    .password-options{
        text-align: center;
    }
</style>
